import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User, AuthState } from '../types';
import { authApi, is2FARequired } from '../services/api';

// 2FA pending state returned when login requires 2FA verification
export interface TwoFAPendingState {
  tempToken: string;
  email: string;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<TwoFAPendingState | void>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<string>;
  verifyEmail: (token: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<string>;
  resetPassword: (token: string, password: string) => Promise<string>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Storage keys
const STORAGE_KEY_USER = 'polaris_user';
const STORAGE_KEY_TOKEN = 'polaris_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Check for stored auth token on mount
    const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
    const storedUser = localStorage.getItem(STORAGE_KEY_USER);

    if (storedToken) {
      // Set token immediately
      setToken(storedToken);

      // If we have cached user data, use it temporarily while verifying
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser) as User;
          setState({ user, isAuthenticated: true, isLoading: true });
        } catch {
          // Invalid JSON, will be refreshed from /me
        }
      }

      // Always call /me to verify token and get fresh user data
      authApi.me(storedToken).then(response => {
        if (response.error || !response.data) {
          // Token is invalid, clear auth
          localStorage.removeItem(STORAGE_KEY_TOKEN);
          localStorage.removeItem(STORAGE_KEY_USER);
          setToken(null);
          setState({ user: null, isAuthenticated: false, isLoading: false });
        } else {
          // Update user data from /me response (includes membershipValidTill, isExpired)
          const userData = response.data.user;
          const updatedUser: User = {
            id: userData.id,
            email: userData.email,
            name: userData.name,
            membershipTier: userData.membershipTier,
            membershipValidTill: userData.membershipValidTill,
            isExpired: userData.isExpired,
          };
          localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(updatedUser));
          setState({ user: updatedUser, isAuthenticated: true, isLoading: false });
        }
      });
    } else {
      setState(s => ({ ...s, isLoading: false }));
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<TwoFAPendingState | void> => {
    const response = await authApi.login(email, password);

    if (response.error || !response.data) {
      throw new Error(response.error || 'Login failed');
    }

    // Check if 2FA is required
    if (is2FARequired(response.data)) {
      // Return 2FA pending state - don't authenticate yet
      return {
        tempToken: response.data.temp_token,
        email: email,
      };
    }

    // No 2FA - proceed with normal login
    const { user: userData, token: authToken } = response.data;

    const user: User = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      membershipTier: userData.membershipTier,
      membershipValidTill: userData.membershipValidTill,
      isExpired: userData.isExpired,
    };

    // Store in localStorage
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEY_TOKEN, authToken);

    setToken(authToken);
    setState({ user, isAuthenticated: true, isLoading: false });
  }, []);

  const verify2FA = useCallback(async (tempToken: string, code: string) => {
    const response = await authApi.verify2FA(tempToken, code);

    if (response.error || !response.data) {
      throw new Error(response.error || '2FA verification failed');
    }

    const { user: userData, token: authToken } = response.data;

    const user: User = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      membershipTier: userData.membershipTier,
      membershipValidTill: userData.membershipValidTill,
      isExpired: userData.isExpired,
    };

    // Store in localStorage
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEY_TOKEN, authToken);

    setToken(authToken);
    setState({ user, isAuthenticated: true, isLoading: false });
  }, []);

  const register = useCallback(async (email: string, password: string, name: string): Promise<string> => {
    const response = await authApi.register(name, email, password);

    if (response.error || !response.data) {
      throw new Error(response.error || 'Registration failed');
    }

    // Registration now sends verification email - return the message
    return response.data.message;
  }, []);

  const verifyEmail = useCallback(async (verificationToken: string) => {
    setState(s => ({ ...s, isLoading: true }));

    const response = await authApi.verifyEmail(verificationToken);

    if (response.error || !response.data) {
      setState(s => ({ ...s, isLoading: false }));
      throw new Error(response.error || 'Email verification failed');
    }

    const { user: userData, token: authToken } = response.data;

    const user: User = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      membershipTier: userData.membershipTier,
      membershipValidTill: userData.membershipValidTill,
      isExpired: userData.isExpired,
    };

    // Store in localStorage
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEY_TOKEN, authToken);

    setToken(authToken);
    setState({ user, isAuthenticated: true, isLoading: false });
  }, []);

  const forgotPassword = useCallback(async (email: string): Promise<string> => {
    const response = await authApi.forgotPassword(email);

    if (response.error || !response.data) {
      throw new Error(response.error || 'Failed to send reset email');
    }

    return response.data.message;
  }, []);

  const resetPassword = useCallback(async (resetToken: string, password: string): Promise<string> => {
    const response = await authApi.resetPassword(resetToken, password);

    if (response.error || !response.data) {
      throw new Error(response.error || 'Password reset failed');
    }

    return response.data.message;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    setToken(null);
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!storedToken) return;

    const response = await authApi.me(storedToken);
    if (response.error || !response.data) {
      // Token is invalid, clear auth
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER);
      setToken(null);
      setState({ user: null, isAuthenticated: false, isLoading: false });
    } else {
      // Update user data from /me response
      const userData = response.data.user;
      const updatedUser: User = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        membershipTier: userData.membershipTier,
        membershipValidTill: userData.membershipValidTill,
        isExpired: userData.isExpired,
      };
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(updatedUser));
      setState({ user: updatedUser, isAuthenticated: true, isLoading: false });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, verify2FA, register, verifyEmail, forgotPassword, resetPassword, logout, refreshUser, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
